import {portWithWrites, portWritesBrand, type PortEnv, type PortWithWrites, type PortWriteIntent} from '../../port-contract';
const lane = Symbol('K3 write-intent lane');
type WriteEnv<Deps extends object> = PortEnv<Deps> & {[lane]?: PortWriteIntent[]};

/** A decision owns its ordered dependency intents; nested decisions share the lane. */
export function withWriteIntents<T, Deps extends object>(env: PortEnv<Deps>, decide: (env: PortEnv<Deps>) => T): T | PortWithWrites<T> {
    if ((env as WriteEnv<Deps>)[lane]) return decide(env);
    const writes: PortWriteIntent[] = [];
    const deps = new Proxy(env.deps, {
        get(target, name, receiver) {
            const dependency = Reflect.get(target, name, receiver) as unknown;
            if (typeof dependency !== 'function') return dependency;
            return (...args: unknown[]): unknown => {
                const result: unknown = Reflect.apply(dependency, target, args);
                if (isWithWrites(result)) {
                    writes.push(...result.writes);
                    return result.value;
                }
                return result;
            };
        },
    });
    const result = decide(Object.assign({...env, deps}, {[lane]: writes}));
    if (isWithWrites(result)) {
        writes.push(...result.writes);
        return portWithWrites(result.value, writes) as T | PortWithWrites<T>;
    }
    return writes.length ? portWithWrites(result, writes) : result;
}

/** Consume a locally called decision inside its parent's ordered lane. */
export function decisionValue<T, Deps extends object>(env: PortEnv<Deps>, result: T | PortWithWrites<T>): T {
    if (!isWithWrites(result)) return result;
    const writes = (env as WriteEnv<Deps>)[lane];
    if (!writes) throw new Error('A nested writer requires its parent write-intent lane');
    writes.push(...result.writes);
    return result.value as T;
}

function isWithWrites(value: unknown): value is PortWithWrites<unknown> {
    return value !== null && typeof value === 'object' && (value as PortWithWrites<unknown>)[portWritesBrand] === true;
}
